<template>
  <el-dialog
    :title="'任务详情'"
    :visible.sync="dialogVisible"
    width="1200px"
    append-to-body
    class="task-detail-dialog"
  >
    <div v-if="taskData">
      <div class="section-title">基础信息</div>
      <div class="custom-desc" style="margin-bottom: 20px;">
        <div class="desc-row">
          <div class="desc-item">
            <div class="desc-label">任务名称</div>
            <div class="desc-value">{{ taskData.taskName || '-' }}</div>
          </div>
          <div class="desc-item">
            <div class="desc-label">计划日期</div>
            <div class="desc-value">{{ formatDate(taskData.executeDate) }}</div>
          </div>
          <div class="desc-item">
            <div class="desc-label">消防系统</div>
            <div class="desc-value">{{ taskData.systemName || '-' }}</div>
          </div>
        </div>
        <div class="desc-row">
          <div class="desc-item">
            <div class="desc-label">完成进度</div>
            <div class="desc-value">{{ taskData.completedDevices || 0 }} / {{ taskData.totalDevices || 0 }}</div>
          </div>
          <div class="desc-item">
            <div class="desc-label">状态</div>
            <div class="desc-value">
              <el-tag :type="getStatusTag(taskData.status)" size="small" effect="light">
                {{ taskData.statusName != null && taskData.statusName !== '' ? taskData.statusName : taskData.status }}
              </el-tag>
            </div>
          </div>
          <div class="desc-item">
            <div class="desc-label">异常数量</div>
            <div class="desc-value">
              <span :class="{ 'abnormal-count': abnormalDeviceCount > 0 }">{{ abnormalDeviceCount }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="section-title">分配信息</div>
      <div class="custom-desc" style="margin-bottom: 20px;">
        <div class="desc-row">
          <div class="desc-item">
            <div class="desc-label">分配方式</div>
            <div class="desc-value">{{ taskData.assignTypeName || taskData.assignType || '不指定' }}</div>
          </div>
          <div class="desc-item">
            <div class="desc-label">
              {{ taskData.assignType === 'team' ? '执行小组' : '执行人员' }}
            </div>
            <div class="desc-value">
              <el-tooltip
                v-if="taskData.assignType === 'user' && getAssignTargetText(taskData) !== '-'"
                :content="getAssignTargetText(taskData)"
                placement="top"
                effect="dark"
              >
                <span class="ellipsis-text">{{ getAssignTargetText(taskData) }}</span>
              </el-tooltip>
              <span v-else>{{ getAssignTargetText(taskData) }}</span>
            </div>
          </div>
          <div class="desc-item">
            <div class="desc-label">组内委派</div>
            <div class="desc-value">
              <template v-if="taskData.assignType === 'team'">
                <span v-if="taskData.teamAssigneeName">
                  <el-tag type="primary" size="small" effect="light">{{ taskData.teamAssigneeName }}</el-tag>
                </span>
                <span v-else style="color:#c0c4cc">-</span>
              </template>
              <template v-else>
                <span style="color:#c0c4cc">-</span>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- 嵌套的任务明细表组件，key 随弹窗打开次数变化，确保每次打开都重新挂载并重新加载数据 -->
      <div class="section-title">任务详情</div>
      <TaskDetailTable
        :key="detailTableKey"
        :task-id="taskData.uuid"
        :zone-area-id="zoneAreaId"
        :area-tree="areaTree"
      />
    </div>
    <div slot="footer">
      <el-button @click="dialogVisible = false">关闭</el-button>
    </div>
  </el-dialog>
</template>

<script>
import TaskDetailTable from './taskDetailTable.vue';

export default {
  name: 'TaskDetailDialog',
  components: {
    TaskDetailTable
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    taskData: {
      type: Object,
      default: null
    },
    /** 任务组执行大区（与列表路由一致），用于明细内区域筛选仅展示本大区子树 */
    zoneAreaId: {
      type: String,
      default: null
    },
    /** 完整区域树，由列表页传入避免重复请求 */
    areaTree: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      // 每次弹窗打开自增，作为 TaskDetailTable 的 key 强制重新挂载
      detailTableKey: 0
    };
  },
  computed: {
    dialogVisible: {
      get () {
        return this.visible;
      },
      set (val) {
        this.$emit('update:visible', val);
      }
    },
    abnormalDeviceCount () {
      const t = this.taskData;
      if (!t) return 0;
      const raw = t.abnormalDeviceCount;
      if (raw == null || raw === '') return 0;
      const n = Number(raw);
      return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
    }
  },
  methods: {
    formatDate (date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString().replace(/\//g, '-');
    },
    getStatusTag (status) {
      const map = { 0: 'info', 1: 'primary', 2: 'success' };
      return map[status] || '';
    },
    getAssignTargetText (task) {
      if (!task || !task.assignType || task.assignType === 'none') return '-';
      if (task.assignType === 'team') return task.assignTeamName || task.assignTeamId || '-';
      return task.assignUserNames || task.assignUserIds || '-';
    }
  },
  watch: {
    visible (val) {
      if (val) {
        this.detailTableKey++;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 10px;
  padding-left: 10px;
  border-left: 4px solid #409eff;
  line-height: 1.1;
}

.custom-desc {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: hidden;
  font-size: 14px;
  background: #fff;
}

.desc-row {
  display: flex;
  border-bottom: 1px solid #ebeef5;

  &:last-child {
    border-bottom: none;
  }
}

.desc-item {
  display: flex;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  border-right: 1px solid #ebeef5;

  &:last-child {
    border-right: none;
  }
}

.desc-label {
  width: 110px;
  background-color: #f8f9fb;
  color: #606266;
  padding: 10px 12px;
  box-sizing: border-box;
  font-weight: 500;
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.desc-value {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  color: #303133;
  box-sizing: border-box;
  word-break: break-all;
  display: flex;
  align-items: center;
  overflow: hidden;
}

/* 指定人员可能很长：单行省略，悬停展示完整文本 */
.ellipsis-text {
  flex: 1;
  min-width: 0;
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  word-break: normal;
}

.abnormal-count {
  color: #f56c6c;
  font-weight: 600;
}
</style>
